import type { GraphQLResult } from 'aws-amplify/api'
import type { Subscription, User, UserInput } from '@skop-app/lib-domain/types'
import type { SeatAwareUserTeam } from '@/types/seatAccess'
import {
  getQuotaErrorCode,
  getQuotaErrorMessage,
} from '@/services/errors/quotaErrors'

/** A projection of actual stored fields, never a User model or Cognito session. */
export type SupportWorkspace = {
  version: 1
  actorUserID: string
  targetUserID: string
  profile: UserInput
  memberships: SeatAwareUserTeam[]
  personalSubscriptions: Subscription[]
  personalShops: NonNullable<User['shop']>[]
  selectedOrganizationID?: string | null
}

export type SupportTarget = {
  actorUserID: string
  targetUserID: string
  sessionScopeKey: string
}

export const supportWorkspaceQuery = /* GraphQL */ `
  query ConsoleSupportWorkspace($targetUserID: ID!, $organizationID: ID) {
    consoleSupportWorkspace(
      targetUserID: $targetUserID
      organizationID: $organizationID
    ) {
      payload
    }
  }
`

const SUPPORT_ERROR_MESSAGES: Record<string, string> = {
  TARGET_NOT_FOUND: 'Utilisateur cible introuvable.',
  TARGET_IDENTITY_INVALID:
    'L’identité de cet utilisateur n’a pas pu être confirmée.',
  TARGET_IDENTITY_AMBIGUOUS: 'L’identité de cet utilisateur est ambiguë.',
  TARGET_DELETED: 'Cet utilisateur n’est plus actif.',
  TARGET_SCOPE_DENIED: 'Cet utilisateur n’a pas accès à cette équipe.',
  Unauthorized:
    'Vous n’êtes pas autorisé à effectuer cette action dans cet espace.',
}

export class SupportRequestError extends Error {
  constructor(readonly code: string) {
    super(
      getQuotaErrorMessage({ code }) ??
        SUPPORT_ERROR_MESSAGES[code] ??
        'Le contexte Support est indisponible. Réessayez.',
    )
    this.name = 'SupportRequestError'
  }
}

const SUPPORT_ERROR_ALIASES: Record<string, string> = {
  SupportTargetInvalid: 'TARGET_IDENTITY_INVALID',
  SupportTargetNotFound: 'TARGET_NOT_FOUND',
  SupportTargetUnavailable: 'TARGET_DELETED',
  SupportScopeDenied: 'TARGET_SCOPE_DENIED',
}

export function supportErrorCode(error: unknown): string {
  const quota = getQuotaErrorCode(
    Array.isArray(error) ? { errors: error } : error,
  )
  if (quota) return quota
  const text =
    error instanceof Error
      ? `${error.name} ${error.message} ${JSON.stringify((error as Error & { errors?: unknown }).errors) ?? ''}`
      : (JSON.stringify(error) ?? '')
  const alias = Object.keys(SUPPORT_ERROR_ALIASES).find((code) =>
    text.includes(code),
  )
  return alias
    ? SUPPORT_ERROR_ALIASES[alias]
    : (Object.keys(SUPPORT_ERROR_MESSAGES).find((code) =>
        text.includes(code),
      ) ?? 'SUPPORT_UNAVAILABLE')
}

/** Do not expose raw GraphQL errors, identity payloads or server exceptions in UI. */
export function readSupportPayload<T>(
  result: GraphQLResult<Record<string, { payload?: unknown } | null>>,
  field: string,
): T {
  if (result.errors?.length)
    throw new SupportRequestError(supportErrorCode(result.errors))
  const raw = result.data?.[field]?.payload
  if (raw == null) throw new SupportRequestError('SUPPORT_UNAVAILABLE')
  try {
    return (typeof raw === 'string' ? JSON.parse(raw) : raw) as T
  } catch {
    throw new SupportRequestError('SUPPORT_UNAVAILABLE')
  }
}

export function assertSupportWorkspace(
  workspace: SupportWorkspace,
  actorUserID: string,
  targetUserID: string,
): void {
  if (
    !workspace ||
    workspace.version !== 1 ||
    workspace.actorUserID !== actorUserID ||
    workspace.targetUserID !== targetUserID ||
    workspace.profile?.id !== targetUserID ||
    !Array.isArray(workspace.memberships) ||
    !Array.isArray(workspace.personalSubscriptions) ||
    !Array.isArray(workspace.personalShops) ||
    workspace.memberships.some(
      (member) =>
        !member.id ||
        !member.value ||
        !['DONE', 'PENDING'].includes(member.status),
    )
  ) {
    throw new SupportRequestError('TARGET_IDENTITY_INVALID')
  }
  if (
    workspace.selectedOrganizationID &&
    !workspace.memberships.some(
      (member) =>
        member.value === workspace.selectedOrganizationID &&
        member.status === 'DONE',
    )
  ) {
    throw new SupportRequestError('TARGET_SCOPE_DENIED')
  }
}

/** Explicit domain projection: no generic UserSchema, missing values stay missing. */
export function adaptSupportWorkspaceProjection(
  workspace: SupportWorkspace,
): User {
  const profile = workspace.profile
  return {
    user: {
      id: profile.id,
      createdAt: profile.createdAt,
      name: profile.name,
      email: profile.email,
      phone: profile.phone,
      SIRET: profile.SIRET,
      type: profile.type,
      VAT: profile.VAT,
      address: profile.address,
      zipcode: profile.zipcode,
      city: profile.city,
      country: profile.country,
      company: profile.company,
      authorization: profile.authorization,
      source: profile.source,
      language: profile.language,
      preferences: profile.preferences,
    },
    organizations: workspace.memberships,
    // Array selection can be absent, as in the existing domain user adapter.
    // No synthetic FREE subscription is inserted into this projection.
    subscription: [...workspace.personalSubscriptions].sort((a, b) => {
      const active = (s: Subscription) =>
        Number(s.status === 'ACTIVE' || s.status === 'TRIALING')
      return (
        active(b) - active(a) || (b.start ?? '').localeCompare(a.start ?? '')
      )
    })[0],
    shopID: workspace.personalShops[0]?.id,
    shop: workspace.personalShops[0],
  }
}

export function supportQueryKey<T extends readonly unknown[]>(
  key: T,
  target?: SupportTarget | null,
  ...dimensions: unknown[]
) {
  return target
    ? ([...key, 'support', target.sessionScopeKey, ...dimensions] as const)
    : key
}

export function assertSupportReadCurrent(signal?: AbortSignal): void {
  signal?.throwIfAborted()
}
